<!-- src/components/Profile/AccountSettingsModal.vue -->
<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import AccountSummary from "./AccountSummary.vue";
import ProfileForm from "./ProfileForm.vue";
import AdditionalProfileDetails from "./AdditionalProfileDetails.vue";
import ChangePasswordForm from "./ChangePasswordForm.vue";
import { useDraggableModal } from "@/composables/useDraggableModal";

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

// ✅ PATCH:
// Quando outro modal estiver aberto acima deste, como o catálogo de planos,
// o AccountSettingsModal não deve reagir a atalhos globais como Escape/Enter.
const props = defineProps({
  suspendKeyboardShortcuts: {
    type: Boolean,
    default: false,
  },

  // ✅ NOVO: contador incrementado pelo pai (DashboardLayout) quando o
  // UpgradeModal (catálogo de planos) é fechado. Propagado ao AccountSummary
  // via :reload-signal para que ele recarregue currentUser/assinaturas/cotas —
  // caso contrário, como o AccountSummary fica montado durante toda a vida
  // deste modal, ele exibe dados stale do plano anterior após um upgrade
  // bem-sucedido.
  //
  // Antes este prop era declarado mas o DashboardLayout não o passava — o fio
  // estava solto. Agora o DashboardLayout incrementa um contador a cada
  // fechamento do UpgradeModal e o entrega aqui.
  upgradeModalClosedSignal: {
    type: Number,
    default: 0,
  },
});

// ✅ MUDANÇA: 'subscriptionChanged' adicionado.
//
// O AccountSummary emite 'subscriptionChanged' sempre que uma ação altera a
// assinatura (cancelar, pausar, reativar, estornar). Este modal apenas repassa
// o evento ao pai (DashboardLayout), que reexecuta o refresh das stores para
// que o badge de plano no header reflita o novo estado.
const emit = defineEmits(["close", "openUpgradeModal", "subscriptionChanged"]);

// 'summary' | 'password' | 'personal' | 'social' | 'additional'
const activeView = ref("summary");

const profileFormRef = ref(null);
const additionalDetailsRef = ref(null);
const passwordFormRef = ref(null); // ✅ ref para o componente de senha

const isSaving = ref(false);

async function saveChanges() {
  // Só faz sentido salvar se tivermos o ProfileForm
  if (!profileFormRef.value) return;

  isSaving.value = true;

  try {
    let additionalPayload = null;

    if (
      additionalDetailsRef.value &&
      typeof additionalDetailsRef.value.getAdditionalPayload === "function"
    ) {
      additionalPayload = additionalDetailsRef.value.getAdditionalPayload();
    }

    const updateSuccessful = await profileFormRef.value.handleUpdate(
      additionalPayload
    );

    if (updateSuccessful) {
      profileFormRef.value.closeForm();
    }
  } catch (error) {
    console.error("Falha ao salvar as alterações:", error);
  } finally {
    isSaving.value = false;
  }
}

function closeModal(event = null) {
  // ✅ PATCH:
  // Proteção defensiva para eventos DOM.
  // Mantém compatibilidade com chamadas sem evento, como @formClosed.
  event?.preventDefault?.();
  event?.stopPropagation?.();

  emit("close");
}

function handleOpenUpgradeModal(event = null) {
  // ✅ PATCH CRÍTICO:
  // Antes este método chamava closeModal(), o que fechava o AccountSettingsModal
  // e desmontava o AccountSummary antes mesmo de abrir o catálogo de planos.
  //
  // Agora ele apenas solicita ao pai a abertura do modal de upgrade,
  // mantendo o AccountSummary aberto por baixo.
  event?.preventDefault?.();
  event?.stopPropagation?.();

  emit("openUpgradeModal");
}

function handleEscKey(event) {
  // ✅ PATCH:
  // Se o modal de upgrade/catálogo estiver aberto acima deste modal,
  // o Escape deve ser tratado pelo modal superior, não pelo AccountSettingsModal.
  if (props.suspendKeyboardShortcuts) return;

  if (event.key === "Escape") {
    closeModal(event);
  }
}

/**
 * 🎯 Enter só dispara salvar nas abas que usam ProfileForm/Additional
 * (não interfere na aba de senha, que tem submit próprio via botão)
 */
function handleEnterKey(event) {
  // ✅ PATCH:
  // Evita que Enter dentro de outro modal aberto por cima deste
  // acione salvamento no AccountSettingsModal.
  if (props.suspendKeyboardShortcuts) return;

  if (event.key === "Enter" || event.keyCode === 13) {
    if (event.target.tagName === "TEXTAREA") return;
    if (event.target.tagName === "BUTTON") return;

    event.preventDefault();

    if (
      activeView.value === "personal" ||
      activeView.value === "social" ||
      activeView.value === "additional"
    ) {
      saveChanges();
    }
  }
}

onMounted(() => {
  document.addEventListener("keydown", handleEscKey);
  document.addEventListener("keydown", handleEnterKey);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleEscKey);
  document.removeEventListener("keydown", handleEnterKey);
});
</script>

<template>
  <div class="modal-overlay">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <header
        class="main-header"
        :class="{
 'main-header--draggable': isDraggable,
          'main-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h2 class="main-title">Perfil</h2>
        <button
          @click.stop.prevent="closeModal"
          class="close-button"
          aria-label="Fechar modal"
        >
          <i class="fas fa-times"></i>
        </button>
      </header>

      <div class="panel-body">
        <main class="content-area">
          <!-- 🔝 Abas no topo -->
          <nav class="settings-tabs">
            <ul>
              <!-- Resumo -->
              <li>
                <button
                  type="button"
                  :class="['tab-btn', { active: activeView === 'summary' }]"
                  @click="activeView = 'summary'"
                >
                  <i class="fas fa-file-invoice"></i>
                  <span>Resumo da Conta</span>
                </button>
              </li>

              <!-- 🆕 Alterar Senha (logo após Resumo) -->
              <li>
                <button
                  type="button"
                  :class="['tab-btn', { active: activeView === 'password' }]"
                  @click="activeView = 'password'"
                >
                  <i class="fas fa-lock"></i>
                  <span>Alterar senha</span>
                </button>
              </li>

              <!-- Dados pessoais -->
              <li>
                <button
                  type="button"
                  :class="['tab-btn', { active: activeView === 'personal' }]"
                  @click="activeView = 'personal'"
                >
                  <i class="fas fa-user-edit"></i>
                  <span>Dados Pessoais</span>
                </button>
              </li>

              <!-- Redes sociais -->
              <li>
                <button
                  type="button"
                  :class="['tab-btn', { active: activeView === 'social' }]"
                  @click="activeView = 'social'"
                >
                  <i class="fas fa-share-alt"></i>
                  <span>Redes sociais</span>
                </button>
              </li>

              <!-- Demais dados -->
              <li>
                <button
                  type="button"
                  :class="['tab-btn', { active: activeView === 'additional' }]"
                  @click="activeView = 'additional'"
                >
                  <i class="fas fa-id-card-alt"></i>
                  <span>Demais dados</span>
                </button>
              </li>
            </ul>
          </nav>

          <!-- Conteúdo da aba selecionada -->
          <div class="tab-content">
            <!-- Resumo -->
            <!-- ✅ MUDANÇA: propaga o sinal de fechamento do UpgradeModal para
                 que o AccountSummary recarregue currentUser/assinaturas/cotas
                 após um upgrade bem-sucedido.
                 ✅ MUDANÇA: repassa 'subscriptionChanged' ao pai para que o
                 DashboardLayout recalcule o badge de plano após cancelar/
                 pausar/reativar/estornar uma assinatura. -->
            <AccountSummary
              v-if="activeView === 'summary'"
              :reload-signal="upgradeModalClosedSignal"
              @openUpgradeModal="handleOpenUpgradeModal"
              @subscriptionChanged="$emit('subscriptionChanged')"
            />

            <!-- ProfileForm: Dados pessoais / Redes sociais -->
            <!-- ProfileForm: sempre montado; visível só em 'personal' e 'social' -->
            <ProfileForm
              ref="profileFormRef"
              :isVisible="activeView === 'personal' || activeView === 'social'"
              :section="activeView === 'social' ? 'social' : 'personal'"
              @formClosed="closeModal"
            />

            <!-- Demais dados -->
            <!-- Demais dados: componente sempre montado, só esconde/mostra visualmente -->
            <AdditionalProfileDetails
              ref="additionalDetailsRef"
              :isVisible="activeView === 'additional'"
            />

            <!-- Alterar senha -->
            <ChangePasswordForm
              v-if="activeView === 'password'"
              ref="passwordFormRef"
            />
          </div>

          <!-- Rodapé local com botão Salvar -->
          <div
            v-if="
              activeView === 'personal' ||
              activeView === 'social' ||
              activeView === 'additional' ||
              activeView === 'password'
            "
            class="local-save-footer"
          >
            <!-- Botão para as abas que usam ProfileForm -->
            <button
              v-if="
                activeView === 'personal' ||
                activeView === 'social' ||
                activeView === 'additional'
              "
              type="button"
              @click="saveChanges"
              class="btn btn-success"
              :disabled="
                isSaving ||
                (activeView === 'personal' &&
                  profileFormRef &&
                  profileFormRef.canSubmit === false)
              "
            >
              <span v-if="isSaving" class="spinner"></span>
              <svg
                v-else
                class="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                  clip-rule="evenodd"
                />
              </svg>
              {{ isSaving ? "Salvando..." : "Salvar Alterações" }}
            </button>

            <!-- Botão específico para Alterar senha -->
            <button
              v-else-if="activeView === 'password'"
              type="button"
              class="btn btn-success"
              @click="
                passwordFormRef &&
                  passwordFormRef.submit &&
                  passwordFormRef.submit()
              "
            >
              <svg
                class="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                  clip-rule="evenodd"
                />
              </svg>
              Salvar nova senha
            </button>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-panel {
  max-width: 820px;
  height: min(92vh, 860px);
}

/* O .main-header global só dá fundo e padding; o flex é daqui (título à
   esquerda, X no canto direito). */
.main-header {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* 🔹 NOVO: cabeçalho como alça de arrasto do painel.
   touch-action: none entrega o gesto ao pointermove em vez da rolagem. */
.main-header--draggable {
  cursor: move;
  user-select: none;
  touch-action: none;
}
.main-header--dragging {
  cursor: grabbing;
}
/* O botão de fechar continua sendo clique, não alça */
.main-header--draggable .close-button {
  cursor: pointer;
}

.main-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}

.close-button {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.375rem;
  border-radius: 8px;
  color: var(--text-light);
  font-size: 1.1rem;
  line-height: 1;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}
.close-button:hover {
  background-color: rgba(0, 0, 0, 0.08);
  color: var(--text-dark);
}
.close-button:active {
  transform: translateY(1px);
}
.close-button:focus,
.close-button:focus-visible {
  outline: none;
  box-shadow: none;
}

/* BODY */
.panel-body {
  flex-grow: 1;
  overflow: hidden;
}

.content-area {
  height: 100%;
  overflow-y: auto;
  padding: 0.75rem 1rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

/* 🔝 Abas no topo */
.settings-tabs {
  flex-shrink: 0;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 0.4rem;
  margin-bottom: 0.6rem;
}

.settings-tabs ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-dark);
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}

.tab-btn i {
  font-size: 0.9rem;
}

.tab-btn:hover {
  background-color: var(--brand-tint);
}

.tab-btn:active {
  transform: translateY(1px);
}

.tab-btn:focus,
.tab-btn:focus-visible {
  outline: none;
  box-shadow: none;
}

.tab-btn.active {
  background-color: var(--brand-soft);
  color: #4338ca;
}

/* Área interna da aba */
.tab-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

/* Botão de salvar local – alinhado à direita */
.local-save-footer {
  margin-top: 0.8rem;
  padding-top: 0.6rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end; /* 👉 direita */
}

.spinner {
  width: 14px;
  margin-right: 0.4rem;
}

/* Responsividade */
@media (max-width: 900px) {
  .modal-panel {
    height: min(92vh, 820px);
    border-radius: 14px;
  }
}

@media (max-width: 720px) {
  .modal-overlay {
    padding: 0.75rem;
    padding-top: 2.5vh;
  }

  .content-area {
    padding-inline: 0.9rem;
  }

  .settings-tabs ul {
    overflow-x: auto;
  }
}

@media (max-width: 420px) {
  .main-title {
    font-size: 1rem;
  }

  .tab-btn i {
    display: none;
  }
}
</style>